import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Boxes, CalendarCheck, Clock, Users, ArrowRight, PlayCircle, CheckCircle2, Download, UserCheck, CalendarOff } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";
import ForemanLock from "@/components/app/ForemanLock";
import RevenueCharts from "@/components/app/RevenueCharts";
import DailyReports from "@/components/app/DailyReports";
import CashToday from "@/components/app/CashToday";
import { toast } from "sonner";

const kpis = [
  { key: "today_jobs", label: "Oggi", icon: CalendarCheck, color: "sky" },
  { key: "in_progress", label: "In Corso", icon: PlayCircle, color: "amber" },
  { key: "scheduled", label: "Programmati", icon: Clock, color: "indigo" },
  { key: "completed", label: "Completati", icon: CheckCircle2, color: "emerald" },
  { key: "employees", label: "Dipendenti", icon: Users, color: "slate" },
  { key: "employees_working_today", label: "Al Lavoro Oggi", icon: UserCheck, color: "emerald" },
  { key: "employees_on_leave_today", label: "In Riposo", icon: CalendarOff, color: "rose" },
  { key: "total_jobs", label: "Totali", icon: Boxes, color: "sky" },
];

const colorMap = {
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [todayJobs, setTodayJobs] = useState([]);
  const [exportMonth, setExportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/jobs", { params: { date_from: today, date_to: today } }).then((r) => setTodayJobs(r.data));
  }, []);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/export/jobs", { params: { month: exportMonth }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `traslochi_${exportMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export generato");
    } catch (_e) {
      toast.error("Errore durante l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-10">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Panoramica</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Dashboard Operativa
          </h1>
          <p className="text-slate-500 mt-2 text-sm max-w-xl">
            Monitora in tempo reale lo stato dei traslochi, le squadre al lavoro e la pianificazione dei prossimi giorni.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="inline-flex items-center gap-2 bg-white rounded-xl border border-slate-200 p-1.5 pl-3">
            <input
              type="month"
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              className="bg-transparent text-sm font-mono text-slate-700 focus:outline-none"
              data-testid="export-month-input"
            />
            <button
              onClick={downloadExport}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60"
              data-testid="export-excel-button"
            >
              <Download className="w-3.5 h-3.5" /> {exporting ? "…" : "Excel"}
            </button>
          </div>
          <Link
            to="/lavori"
            data-testid="dashboard-go-to-jobs"
            className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-md transition"
          >
            Gestisci Lavori <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="stats-grid">
        {kpis.map((k) => (
          <div
            key={k.key}
            className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow-md hover:border-sky-300 transition group"
            data-testid={`stat-${k.key}`}
          >
            <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${colorMap[k.color]}`}>
              <k.icon className="w-5 h-5" />
            </div>
            <div className="mt-4 font-display text-3xl font-extrabold text-slate-900">
              {stats ? stats[k.key] : "–"}
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1">
              {k.label}
            </div>
          </div>
        ))}
      </section>

      {/* Today's jobs */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold text-slate-900">Traslochi di Oggi</h2>
          <Link to="/calendario" className="text-sm text-sky-600 hover:text-sky-700 font-semibold">
            Vedi calendario →
          </Link>
        </div>
        {todayJobs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
            <Boxes className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500 font-semibold">Nessun trasloco programmato per oggi</div>
            <Link
              to="/lavori"
              className="inline-block mt-4 text-sm text-sky-600 hover:text-sky-700 font-semibold"
            >
              Crea il primo lavoro →
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2" data-testid="today-jobs-list">
            {todayJobs.map((j) => (
              <Link
                key={j.id}
                to={`/lavori/${j.id}`}
                className="group bg-white rounded-2xl border border-slate-200 p-5 hover:border-sky-400 hover:shadow-md transition"
                data-testid={`today-job-${j.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg text-slate-900 truncate">{j.client_name}</div>
                    <div className="text-xs font-mono text-slate-400 mt-0.5">{j.start_time} {j.end_time && `– ${j.end_time}`}</div>
                  </div>
                  <StatusBadge status={j.status} />
                </div>
                <div className="mt-4 space-y-1 text-sm text-slate-600">
                  <div className="truncate"><span className="text-slate-400">Da:</span> {j.address_from}</div>
                  <div className="truncate"><span className="text-slate-400">A:</span> {j.address_to}</div>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(j.assigned_employees || []).map((e) => (
                    <span key={e.id} className="text-[11px] bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2.5 py-0.5 font-semibold">
                      {e.name}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Cassa del giorno — sintesi incasso + lavori di oggi */}
      <CashToday />

      {/* Daily reports widget */}
      <DailyReports />

      {/* Revenue charts - locked behind foreman password */}
      <ForemanLock>
        <RevenueCharts />
      </ForemanLock>
    </div>
  );
}
